// Branded, downloadable/shareable cards drawn with the Canvas 2D API (no network
// — CSP-safe, works offline). Light "white paper" theme so exported PNG/PDF read
// like a clean data sheet. Typeset in Manrope (the brand font, passed in from the
// page so it matches the site + OG cards). Two renderers:
//   drawShareCard  -> the full ranking (ALL rows, dynamic height)
//   drawChartCard  -> the 12-month trend chart (top 6, line-segment legend)
// Both carry a "Cleanor Labs" wordmark + a live-data CTA with the exact study URL.

import { formatVolume, monthLabel, type PopularityBrandRow } from '@/lib/popularity';

export type ShareCardOpts = {
  rows: PopularityBrandRow[];
  palette: string[];
  scopeName: string;
  categoryLabel: string;
  dataThrough: string;
  metric: 'share' | 'absolute';
  pageUrl?: string;
  unit?: string;
  font?: string; // resolved family, e.g. Manrope; falls back to a system stack
};

const W = 1200;
const FALLBACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

// Light theme tokens (kept in one place so both cards match).
const C = {
  bg: '#ffffff',
  ink: '#1a1c26',
  muted: '#61657a',
  faint: '#9aa0b4',
  rank: '#b3b8c8',
  track: '#eef0f5',
  hair: '#e7e9f0',
};

function setupCanvas(canvas: HTMLCanvasElement, h: number): CanvasRenderingContext2D | null {
  const dpr = Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  canvas.width = W * dpr;
  canvas.height = h * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(dpr, dpr);
  return ctx;
}

function truncate(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t}…`;
}

// A tiny type helper so the whole scale reads consistently.
function typeset(font: string) {
  return (weight: number, size: number) => `${weight} ${size}px ${font}, ${FALLBACK}`;
}

function setTracking(ctx: CanvasRenderingContext2D, px: number) {
  // ctx.letterSpacing is supported in modern browsers; a no-op elsewhere.
  (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = `${px}px`;
}

// Shared header: just the "Cleanor Labs" wordmark (left). No icon tile, no eyebrow.
function drawHeader(ctx: CanvasRenderingContext2D, f: ReturnType<typeof typeset>, padX: number) {
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.font = f(700, 25);
  ctx.fillStyle = C.ink;
  ctx.fillText('Cleanor', padX, 70);
  const cw = ctx.measureText('Cleanor').width;
  ctx.fillStyle = C.faint;
  ctx.fillText(' Labs', padX + cw, 70);
}

// Single footer line: study URL (left) + source (right), identical size/weight/colour.
function drawFooter(
  ctx: CanvasRenderingContext2D,
  f: ReturnType<typeof typeset>,
  padX: number,
  y: number,
  displayUrl: string,
) {
  ctx.font = f(500, 17);
  ctx.fillStyle = C.muted;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillText(displayUrl, padX, y);
  ctx.textAlign = 'right';
  ctx.fillText('Source: Google Keyword Planner · CC BY 4.0', W - padX, y);
  ctx.textAlign = 'left';
}

function drawTitle(
  ctx: CanvasRenderingContext2D,
  f: ReturnType<typeof typeset>,
  text: string,
  padX: number,
) {
  let size = 44;
  const maxW = W - padX * 2;
  do {
    ctx.font = f(700, size);
    if (ctx.measureText(text).width <= maxW) break;
    size -= 2;
  } while (size > 28);
  ctx.fillStyle = C.ink;
  ctx.fillText(text, padX, 150);
}

/** Full ranking card (all rows). Returns the logical height it drew. */
export function drawShareCard(canvas: HTMLCanvasElement, opts: ShareCardOpts): number {
  const { rows, palette, scopeName, categoryLabel, dataThrough, metric, pageUrl, unit } = opts;
  const f = typeset(opts.font || FALLBACK);
  const padX = 64;
  const top0 = 228;
  const rowH = 46;
  const H = Math.round(top0 + rows.length * rowH + 62);

  const ctx = setupCanvas(canvas, H);
  if (!ctx) return H;
  const accent = palette[0] || '#5b62ff';
  const displayUrl = (pageUrl || 'https://cleanor.app/trends').replace(/^https?:\/\//, '');

  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, W, 5);

  drawHeader(ctx, f, padX);
  drawTitle(ctx, f, `Most popular ${categoryLabel}`, padX);
  ctx.fillStyle = C.muted;
  ctx.font = f(500, 21);
  ctx.fillText(
    `by real Google search demand · ${scopeName} · ${monthLabel(dataThrough)}`,
    padX,
    184,
  );

  const total = rows.reduce((a, r) => a + r.avg, 0) || 1;
  const value = (r: PopularityBrandRow) => (metric === 'share' ? (r.avg / total) * 100 : r.avg);
  const valueLabel = (r: PopularityBrandRow) =>
    metric === 'share' ? `${Math.round((r.avg / total) * 1000) / 10}%` : formatVolume(r.avg);
  const maxV = Math.max(...rows.map(value), 1);

  const nameX = padX + 46;
  const barX = padX + 300;
  const valueColW = 104;
  const barMaxW = W - padX - barX - valueColW;
  const nameMaxW = barX - nameX - 18;

  // column header
  ctx.fillStyle = C.faint;
  ctx.font = f(600, 12);
  setTracking(ctx, 1.2);
  ctx.fillText((unit || 'Item').toUpperCase(), nameX, top0 - 16);
  ctx.textAlign = 'right';
  ctx.fillText(metric === 'share' ? 'SHARE' : 'SEARCHES / MO', W - padX, top0 - 16);
  setTracking(ctx, 0);
  ctx.textAlign = 'left';
  ctx.strokeStyle = C.hair;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(padX, top0 - 5);
  ctx.lineTo(W - padX, top0 - 5);
  ctx.stroke();

  rows.forEach((r, i) => {
    const y = top0 + i * rowH;
    const color = palette[i % palette.length];
    ctx.fillStyle = C.rank;
    ctx.font = f(600, 18);
    ctx.textAlign = 'right';
    ctx.fillText(String(i + 1), padX + 26, y + 23);
    ctx.fillStyle = C.ink;
    ctx.font = f(600, 21);
    ctx.textAlign = 'left';
    ctx.fillText(truncate(ctx, r.name, nameMaxW), nameX, y + 23);
    const w = Math.max(6, (Math.sqrt(value(r)) / Math.sqrt(maxV)) * barMaxW);
    ctx.fillStyle = C.track;
    roundRect(ctx, barX, y + 7, barMaxW, 18, 9);
    ctx.fill();
    ctx.fillStyle = color;
    roundRect(ctx, barX, y + 7, w, 18, 9);
    ctx.fill();
    ctx.fillStyle = C.ink;
    ctx.font = f(700, 19);
    ctx.textAlign = 'right';
    ctx.fillText(valueLabel(r), W - padX, y + 23);
    ctx.textAlign = 'left';
  });

  drawFooter(ctx, f, padX, H - 34, displayUrl);
  return H;
}

/** 12-month trend chart card (top 6 lines). Fixed 1200x675. */
export function drawChartCard(
  canvas: HTMLCanvasElement,
  opts: ShareCardOpts & { months: string[] },
): number {
  const { rows, palette, scopeName, categoryLabel, dataThrough, metric, pageUrl, months } = opts;
  const f = typeset(opts.font || FALLBACK);
  const H = 675;
  const padX = 64;
  const ctx = setupCanvas(canvas, H);
  if (!ctx) return H;
  const accent = palette[0] || '#5b62ff';
  const displayUrl = (pageUrl || 'https://cleanor.app/trends').replace(/^https?:\/\//, '');

  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, W, 5);
  drawHeader(ctx, f, padX);
  drawTitle(ctx, f, `${categoryLabel}: 12-month trend`, padX);
  ctx.fillStyle = C.muted;
  ctx.font = f(500, 21);
  ctx.fillText(
    `real Google search demand · ${scopeName} · through ${monthLabel(dataThrough)}`,
    padX,
    184,
  );

  const top = rows.slice(0, 6);
  const totals = months.map((_, i) => rows.reduce((a, r) => a + (r.monthly[i] || 0), 0) || 1);
  const valAt = (r: PopularityBrandRow, i: number) =>
    metric === 'share' ? ((r.monthly[i] || 0) / totals[i]) * 100 : r.monthly[i] || 0;
  const maxY = Math.max(
    ...top.flatMap((r) => months.map((_, i) => valAt(r, i))),
    metric === 'share' ? 10 : 1,
  );

  const plotT = 230;
  const plotB = H - 156;
  const plotL = padX + 54;
  const plotR = W - padX;
  const xAt = (i: number) => plotL + (i / (months.length - 1 || 1)) * (plotR - plotL);
  const yAt = (v: number) => plotT + (1 - v / maxY) * (plotB - plotT);

  // gridlines + y labels
  ctx.fillStyle = C.faint;
  ctx.font = f(500, 15);
  const ticks = 4;
  for (let t = 0; t <= ticks; t++) {
    const v = (maxY / ticks) * t;
    const y = yAt(v);
    ctx.strokeStyle = t === 0 ? C.hair : '#f1f2f7';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(plotL, y);
    ctx.lineTo(plotR, y);
    ctx.stroke();
    ctx.textAlign = 'right';
    ctx.fillText(
      metric === 'share' ? `${Math.round(v)}%` : formatVolume(Math.round(v)),
      plotL - 12,
      y + 5,
    );
    ctx.textAlign = 'left';
  }
  // x labels (every other month)
  ctx.fillStyle = C.faint;
  ctx.font = f(500, 14);
  ctx.textAlign = 'center';
  months.forEach((m, i) => {
    if (i % 2 === 0) ctx.fillText(monthLabel(m).replace(' 20', " '"), xAt(i), plotB + 28);
  });
  ctx.textAlign = 'left';

  // lines
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  top.forEach((r, idx) => {
    ctx.strokeStyle = palette[idx % palette.length];
    ctx.lineWidth = 3;
    ctx.beginPath();
    months.forEach((_, i) => {
      const x = xAt(i);
      const y = yAt(valAt(r, i));
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  });

  // legend — short line segments (matches a line chart), not squares
  let lx = padX;
  const ly = H - 104;
  ctx.font = f(600, 17);
  top.forEach((r, idx) => {
    ctx.strokeStyle = palette[idx % palette.length];
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(lx, ly - 5);
    ctx.lineTo(lx + 24, ly - 5);
    ctx.stroke();
    ctx.fillStyle = C.ink;
    ctx.fillText(r.name, lx + 34, ly);
    lx += 34 + ctx.measureText(r.name).width + 30;
  });

  drawFooter(ctx, f, padX, H - 40, displayUrl);
  return H;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export const shareCardWidth = W;

export function shareCardFilename(
  categoryLabel: string,
  scopeName: string,
  dataThrough: string,
  kind = 'ranking',
  ext = 'png',
) {
  const slug = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  return `cleanor-search-index-${slug(categoryLabel)}-${kind}-${slug(scopeName)}-${dataThrough}.${ext}`;
}
